import React, { useState, useEffect, useRef, useContext } from "react";
import { Alert } from "reactstrap";
import Pagination from '../../components/pagination/pagination';
import SubUsersTable from "../../components/subUsersTable";
import SubUsersTableModal from "../../components/subUsersTableModal";
import SubUserSubscriptionModal from "../../components/subUserSubscriptionModal";
import ViewLayout from "../../components/viewLayout";
import TableHelper from "../../components/tableHelper";
import { getCookie } from "../../jwt/_helpers/cookie";
import { useFetch } from "../../utils/fetchHook";
import TenantContext from "../../utils/tenantContext";
import { stripeSubscriptionService } from "../../jwt/_services/stripeSubscription.service";
import { getSubscriptionsEnabled } from "../../utils/subscriptionUtils";

import {StateContext} from "../../utils/context";
import { isDemoLocked } from "../../utils/demoConfig";

const SubUsers = props => {
  const [openModal, setToggleModal] = useState(false);
  const [dataModal, setDataModal] = useState(null);
  const [updateTitle, setUpdateTitle] = useState("");
  const [updateBody, setUpdateBody] = useState("");
  const [updateData, setUpdateData] = useState({});
  const [updateBtnText, setUpdateBtnText] = useState("");
  const [updateBtnColor, setUpdateBtnColor] = useState("");
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [selectedSubscriptionUser, setSelectedSubscriptionUser] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  
  const token = getCookie("token");
  const {currentUser} = useContext(StateContext);
  const { tenant } = useContext(TenantContext);
  const { is_premium_user, is_sub_user, is_employee_account, employee_permission } = currentUser;
  const subscriptionsEnabled = getSubscriptionsEnabled(tenant);

  const didEff1 = useRef(false);
  const didEff2 = useRef(false);
  const [forceUpdate, setForce] = useState(1);
  const [dataCount, setDataCount] = useState(0);
  const [currrentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState('');
  const [ordering, setOrdering] = useState({order: '', name: 'first_name', sort: 'first_name'});
  const [filters, setFilters] = useState({
    is_active: '',
    distribution: '',
    is_blacklisted: '',
  });

  const buildFilterQuery = ({ is_active, distribution, is_blacklisted }) => {
    const params = [];
    if (is_active !== '') {
      params.push(`is_active=${is_active}`);
    }
    if (distribution !== '') {
      // Model field is "disable distribution" — invert UI enabled/disabled filter value.
      params.push(`distribution=${distribution === 'true' ? 'false' : 'true'}`);
    }
    if (is_blacklisted !== '') {
      params.push(`is_blacklisted=${is_blacklisted}`);
    }
    return params.length ? `&${params.join('&')}` : '';
  };

  const filterQuery = buildFilterQuery(filters);

  const [apiData, error, loading] = useFetch("GET",`sub-users/?ordering=${ordering.order + ordering.name}&page=${currrentPage}&page_size=${pageSize}&search=${query}${filterQuery}`,token,false,forceUpdate);
  const [tableData, setTableData] = useState([]);

  //ajax handlers
  const [success, setSuccess] = useState(false);
  const [errorPut, setError] = useState(false);
 
  const [lastAction, setLastAction] = useState("updated");

  useEffect(() => {
    if (!subscriptionsEnabled) {
      setSubscriptionPlans([]);
      return undefined;
    }

    let isMounted = true;

    stripeSubscriptionService
      .listPublicPlansCached(token)
      .then((plans) => {
        if (isMounted) {
          setSubscriptionPlans(plans);
        }
      })
      .catch(() => {
        if (isMounted) {
          setSubscriptionPlans([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [subscriptionsEnabled, token]);

  const handleSubscriptionBadgeClick = (row) => {
    setSelectedSubscriptionUser(row);
    setSubscriptionModalOpen(true);
  };

  const handleSubscriptionModalClose = () => {
    setSubscriptionModalOpen(false);
    setSelectedSubscriptionUser(null);
  };

  useEffect(() => {
    if (apiData?.results) {
      setTableData(apiData.results);
    }
    if (apiData?.count != null) {
      setDataCount(apiData.count);
    }
  }, [apiData]);


  useEffect(() => {
    if (!didEff1.current) {
      didEff1.current = true;
      return;
    }
    setForce(prev => prev + 1);
  }, [currrentPage, pageSize, ordering.order, ordering.name, filters.is_active, filters.distribution, filters.is_blacklisted]);


  useEffect(() => {
    if (!didEff2.current) {
      didEff2.current = true;
      return;
    }
    const timeout = setTimeout(() => {
      setForce(prev => prev + 1);
    }, 500)
    return () => clearTimeout(timeout)
  }, [query])

  const paginationProps = {
    pageSize, setPageSize, dataCount, setCurrentPage, currrentPage
  }

  const defaultSorted = [{
    dataField: ordering.sort,
    order: ordering.order === "-" ? "desc" : "asc"
  }] 

  const handlerValues = { setSuccess, setError, forceUpdate, setForce };

  const handleFilterChange = (name, value) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setCurrentPage(1);
    setQuery('');
    setFilters({
      is_active: '',
      distribution: '',
      is_blacklisted: '',
    });
  };

  const hasActiveFilters =
    query !== '' || filters.is_active !== '' || filters.distribution !== '' || filters.is_blacklisted !== '';

  return (
    <>
      {
        !isDemoLocked() && !currentUser.is_premium_user && (
          <Alert color="danger">
            This feature is offered to Premium Users only. Please contact support team if
            you want to upgrade your account.
          </Alert>        
        )
      }

      {
        loading ? (
          <TableHelper loading />
        ) : error ? (
          <TableHelper error />
        ) :  (
        <div>
          <ViewLayout title={"Your sub-users list"}>
            <SubUsersTable
              setToggleModal={setToggleModal}
              setDataModal={setDataModal}
              setUpdateBody={setUpdateBody}
              setUpdateTitle={setUpdateTitle}
              setUpdateData={setUpdateData}
              setUpdateBtnText={setUpdateBtnText}
              setUpdateBtnColor={setUpdateBtnColor}
              setLastAction={setLastAction}
              forceUpdate={forceUpdate}
              data={tableData}
              defaultSorted={defaultSorted}
              searchQuery={{
                setQuery,
                query,
                ordering,
                setOrdering,
                filters,
                onFilterChange: handleFilterChange,
                resetFilters,
                hasActiveFilters,
              }}
              editDisabled={
                !is_premium_user ||
                is_sub_user ||
                (is_employee_account && employee_permission === "content_only")
              }
              subscriptionsEnabled={subscriptionsEnabled}
              subscriptionPlans={subscriptionPlans}
              onSubscriptionBadgeClick={handleSubscriptionBadgeClick}
            />
          </ViewLayout>
          <Pagination pagination={paginationProps}/>
        </div>
      ) }
        
      
      {dataModal && (
        <SubUsersTableModal
          openModal={openModal}
          setToggleModal={setToggleModal}
          dataModal={dataModal}
          values={handlerValues}
          title={updateTitle}
          body={updateBody}
          updateBtnText={updateBtnText}
          updateBtnColor={updateBtnColor}
          apiURL={`sub-users/${dataModal.id}`}
          bodyID={dataModal.id}
          updateData={updateData}
        />
      )}
      <SubUserSubscriptionModal
        isOpen={subscriptionModalOpen}
        subUser={selectedSubscriptionUser}
        onClose={handleSubscriptionModalClose}
      />
      {success && <Alert color="success">{lastAction}</Alert>}
      {errorPut && (
        <Alert color="danger">
          Something went wrong! Please refresh page and try again!
        </Alert>
      )}
    </>
  );
};

export default SubUsers;
